import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";

import SPECIES from "#/model/SpeciesCodeList.js";
import ApiClient from "#/redux/api/ApiClient";
import { getErrorMessage } from "#/utils/APIUtils";

const baseUrl = "/api/sif/spol";

const initialState = {
  list: [],
  page: {},
  byId: {},
  details: null,
  isLoading: false,
  isListLoading: false,
  error: null,
};

/**
 * GET /api/sif/spol
 * Returns full list of gender codes.
 */
export const fetchGenderCodeList = createAsyncThunk(
  "genderCodeList/fetchAll",
  async (_, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      const response = await ApiClient.get(`${baseUrl}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * GET /api/sif/spol/{sifra}
 */
export const fetchGenderCodeBySifra = createAsyncThunk(
  "genderCodeList/fetchBySifra",
  async (sifra, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      const response = await ApiClient.get(`${baseUrl}/${sifra}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * GET /api/sif/spol/page
 */
export const fetchGenderCodePage = createAsyncThunk(
  "genderCodeList/fetchPage",
  // { page, filter, sort } – all optional
  async ({ page, filter, sort } = {}, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        undefined,
        page,
        filter,
        sort,
        true // stripQueryParamsForCancelPrevious
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * POST /api/sif/spol
 */
export const createGenderCode = createAsyncThunk(
  "genderCodeList/create",
  async (payload, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      const response = await ApiClient.post(`${baseUrl}`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * PUT /api/sif/spol/{sifra}
 */
export const updateGenderCode = createAsyncThunk(
  "genderCodeList/update",
  async ({ sifra, data }, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      const response = await ApiClient.put(`${baseUrl}/${sifra}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * DELETE /api/sif/spol/{sifra}
 */
export const deleteGenderCode = createAsyncThunk(
  "genderCodeList/delete",
  async (sifra, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      await ApiClient.delete(`${baseUrl}/${sifra}`);
      return sifra;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const genderCodeListSlice = createSlice({
  name: "genderCodeList",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // ---- LIST (ALL) ----
    builder.addCase(fetchGenderCodeList.pending, (state) => {
      state.isListLoading = true;
      state.isLoading = true;

      state.error = null;
    });
    builder.addCase(fetchGenderCodeList.fulfilled, (state, action) => {
      state.list = action.payload;
      state.byId = {};
      for (const item of action.payload) {
        if (item?.sifra) {
          state.byId[item.sifra] = item;
        }
      }
      state.isListLoading = false;
      state.isLoading = true;
    });
    builder.addCase(fetchGenderCodeList.rejected, (state, action) => {
      state.error = action.payload;
      state.isListLoading = false;
      state.isLoading = true;
    });

    // ---- SINGLE BY SIFRA ----
    builder.addCase(fetchGenderCodeBySifra.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchGenderCodeBySifra.fulfilled, (state, action) => {
      state.isLoading = false;
      const item = action.payload;
      if (item?.sifra) {
        state.byId[item.sifra] = item;
        state.details = item.sifra;
      }
    });
    builder.addCase(fetchGenderCodeBySifra.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // ---- PAGE ----
    builder.addCase(fetchGenderCodePage.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchGenderCodePage.fulfilled, (state, action) => {
      state.isLoading = false;
      state.page = action.payload;
      const content = action.payload?.content || [];
      state.list = content;
      state.byId = {};
      for (const item of content) {
        if (item?.sifra) {
          state.byId[item.sifra] = item;
        }
      }
    });
    builder.addCase(fetchGenderCodePage.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // ---- CREATE ----
    builder.addCase(createGenderCode.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createGenderCode.fulfilled, (state, action) => {
      state.isLoading = false;
      const item = action.payload;
      if (item?.sifra) {
        state.byId[item.sifra] = item;
        state.list.push(item);
        state.details = item.sifra;
      }
    });
    builder.addCase(createGenderCode.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // ---- UPDATE ----
    builder.addCase(updateGenderCode.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateGenderCode.fulfilled, (state, action) => {
      state.isLoading = false;
      const item = action.payload;
      if (item?.sifra) {
        state.byId[item.sifra] = item;
        const idx = state.list.findIndex((s) => s.sifra === item.sifra);
        if (idx !== -1) {
          state.list[idx] = item;
        }
        state.details = item.sifra;
      }
    });
    builder.addCase(updateGenderCode.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // ---- DELETE ----
    builder.addCase(deleteGenderCode.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteGenderCode.fulfilled, (state, action) => {
      const sifra = action.payload;
      delete state.byId[sifra];
      state.list = state.list.filter((s) => s.sifra !== sifra);
      if (state.details === sifra) {
        state.details = null;
      }
      state.isLoading = false;
    });
    builder.addCase(deleteGenderCode.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });
  },
});

export default genderCodeListSlice.reducer;
export const SHEEP_GOAT_GENDER_CODE_LIST = createSelector(
  (state) => state?.genderCodeList,
  ({ list }) => list?.filter((item) => item?.veljaZaKoze || item?.veljaZaOvce)
);
export const PIG_GENDER_CODE_LIST = createSelector(
  (state) => state?.genderCodeList,
  ({ list }) => list?.filter((gender) => gender.veljaZaPrasice) ?? []
);

export const PIG_BREEDS_LIST = createSelector(
  (state) => state.breedCodeList?.page?.content,
  (breeds) => {
    return breeds?.filter((breed) => breed?.species === SPECIES.PIG.id) ?? [];
  }
);
